"use client"

import { useState, useEffect, useRef, useMemo } from "react"
import type { Hero, HeroSkill } from "@/types/hero.types"
import type { HeroConfig } from "@/data/hero-types"
import { getSkillLevelStats, getSkillCooldownProgress, canUseSkill } from "@/systems/skill-system"
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip"

interface SkillsSectionProps {
  scale: number
  hero?: Hero | null
  heroConfig?: HeroConfig | null
  onUseSkill?: (skillId: string) => void
  pendingSkillId?: string | null // Skill waiting for target selection
  lastUpgradedSkillId?: string | null // Skill that was just upgraded (for animation)
  disableClicks?: boolean // Disable click handlers (for mobile - clicks handled by bottom sheet)
  variant?: "game" | "shop"
}

const SkillTooltip = ({ skill, hero }: { skill: HeroSkill; hero?: Hero }) => {
  const stats = getSkillLevelStats(skill)
  const isMaxLevel = skill.currentLevel >= skill.maxLevel
  const hasCooldown = stats.cooldown > 0
  const isPassive = skill.type === "passive"
  const isUltimate = skill.type === "ultimate"
  const hasCritStats = stats.critChance !== undefined

  // Game variant tooltip has canUse logic, shop variant doesn't
  const { canUse, reason } = hero ? canUseSkill(hero, skill.id) : { canUse: false, reason: undefined }

  // For ultimates, calculate the first level it can be unlocked at
  // Ultimate level 1 unlocks at hero level 6, level 2 at 12, etc.
  const getNextUltimateUnlockLevel = () => {
    if (!isUltimate) return null
    const nextSkillLevel = skill.currentLevel + 1
    if (nextSkillLevel > skill.maxLevel) return null
    return nextSkillLevel * 6 // Level 6 for first unlock, 12 for second, etc.
  }
  const nextUltimateUnlockLevel = getNextUltimateUnlockLevel()

  return (
    <div className="w-[130px] p-1.5">
      {/* Header */}
      <div className="flex items-center justify-between mb-0.5">
        <span className="font-bold text-amber-400 text-[11px]">{skill.name}</span>
        <span className="text-[9px] text-amber-200/70">Lv{skill.currentLevel}</span>
      </div>

      {/* Ultimate indicator with unlock level - only for game variant with hero */}
      {isUltimate && hero && (
        <div className="mb-1">
          <span className="text-[9px] text-purple-400 font-semibold">Ultimate</span>
          {nextUltimateUnlockLevel && hero.level < nextUltimateUnlockLevel && (
            <span className="text-[8px] text-gray-400 ml-1">
              (Lv {nextUltimateUnlockLevel} to upgrade)
            </span>
          )}
        </div>
      )}

      {/* Passive indicator */}
      {isPassive && (
        <div className="mb-1">
          <span className="text-[9px] text-cyan-400 font-semibold">Passive</span>
        </div>
      )}

      {/* Description */}
      <p className="text-[9px] text-gray-300 leading-tight mb-1">{skill.description}</p>

      {/* Stats */}
      <div className="border-t border-amber-600/30 pt-1 space-y-0.5 text-[9px]">
        {hasCritStats ? (
          <>
            {/* Critical hit stats for passive skills like See the Wind */}
            <div className="flex justify-between">
              <span className="text-gray-500">Crit Chance</span>
              <span className="text-cyan-400 font-semibold">
                {((stats.critChance || 0) * 100).toFixed(0)}%
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Crit DMG</span>
              <span className="text-red-400 font-semibold">
                {stats.critMultiplier || 0}x
              </span>
            </div>
          </>
        ) : (
          <>
            {/* Standard combat stats */}
            <div className="flex justify-between">
              <span className="text-gray-500">Damage</span>
              <span className="text-red-400 font-semibold">{stats.damage}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Mana cost</span>
              <span className="text-blue-400">{stats.manaCost}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Cooldown</span>
              <span className={hasCooldown ? "text-yellow-400" : "text-green-400"}>
                {hasCooldown ? `${(stats.cooldown / 60).toFixed(1)}s` : "None"}
              </span>
            </div>
          </>
        )}
      </div>

      {/* Hotkey - only for non-passive skills in game variant */}
      {!isPassive && hero && (
        <div className="mt-1 pt-1 border-t border-amber-600/30 flex justify-between items-center">
          <span className="text-[8px] text-gray-500">Press</span>
          <kbd className="bg-gray-800 text-amber-300 px-1 py-0.5 rounded text-[9px] font-mono">{skill.hotkey}</kbd>
        </div>
      )}

      {/* Warning or upgrade hint */}
      {/* Game variant shows warnings and upgrade hints, shop variant only shows upgrade hint */}
      {hero && !canUse && reason && !isPassive && (
        <div className="mt-1 text-[8px] text-red-400">{reason}</div>
      )}
      {!isMaxLevel && (canUse || isPassive || !hero) && (
        <div className="mt-1 text-[8px] text-green-400/80">Upgrade available</div>
      )}
    </div>
  )
}

export const SkillsSection = ({ scale, hero, heroConfig, onUseSkill, pendingSkillId, lastUpgradedSkillId, disableClicks = false, variant = "game" }: SkillsSectionProps) => {
  const slotSize = 29 * scale
  const gap = 7 * scale
  const left = 254 * scale
  // Shop variant has slightly different top positioning
  const top = variant === "game" ? 63 * scale : 62 * scale

  // Shop variant can fallback to heroConfig skill definitions (no runtime cooldown state)
  const skills = useMemo(
    () =>
      hero?.skills ||
      (variant === "shop" ? heroConfig?.skills.map((s) => ({ ...s, currentCooldown: 0 })) : []) ||
      [],
    [hero?.skills, variant, heroConfig?.skills],
  )

  const sectionId = variant === "game" ? "skills-section" : "shop-skills-section"

  // Track previous cooldown states to detect when cooldown finishes (game variant only)
  const prevCooldownStates = useRef<Record<string, boolean>>({})
  const [cooldownCompleteSkills, setCooldownCompleteSkills] = useState<Set<string>>(new Set())

  // Detect cooldown completion (game variant only)
  useEffect(() => {
    if (variant === "shop") return

    const newCooldownComplete = new Set<string>()

    for (const skill of skills) {
      const wasOnCooldown = prevCooldownStates.current[skill.id] ?? false
      const isOnCooldown = getSkillCooldownProgress(skill) < 1

      // Skill just came off cooldown
      if (wasOnCooldown && !isOnCooldown) {
        newCooldownComplete.add(skill.id)
      }

      prevCooldownStates.current[skill.id] = isOnCooldown
    }

    if (newCooldownComplete.size > 0) {
      setCooldownCompleteSkills((prev) => new Set([...prev, ...newCooldownComplete]))

      // Clear the effect after animation duration
      setTimeout(() => {
        setCooldownCompleteSkills((prev) => {
          const next = new Set(prev)
          for (const skillId of newCooldownComplete) {
            next.delete(skillId)
          }
          return next
        })
      }, 500)
    }
  }, [skills, variant])

  const handleSkillClick = (skill: HeroSkill) => {
    if (!hero || !onUseSkill) return
    const { canUse } = canUseSkill(hero, skill.id)
    if (canUse) {
      onUseSkill(skill.id)
    }
  }

  return (
    <TooltipProvider delayDuration={100}>
      <div
        id={sectionId}
        className="absolute grid grid-cols-2"
        style={{
          left,
          top,
          gap,
        }}
      >
        {[0, 1, 2, 3].map((i) => {
          const skill = skills[i]

          // Empty slot - no skill
          if (!skill) {
            // Game variant uses button, shop variant uses div
            if (variant === "shop") {
              return (
                <div
                  key={i}
                  id={`shop-skill-slot-${i}`}
                  className="bg-gray-800/60 relative"
                  style={{
                    width: slotSize,
                    height: slotSize,
                    borderRadius: 2 * scale,
                  }}
                />
              )
            }

            return (
              <button
                key={i}
                id={`skill-slot-${i}`}
                className="bg-gray-800/60 relative"
                style={{
                  width: slotSize,
                  height: slotSize,
                  borderRadius: 2 * scale,
                }}
                disabled
              />
            )
          }

          const isPassive = skill.type === "passive"

          // Shop variant is non-interactive
          if (variant === "shop") {
            return (
              <Tooltip key={i}>
                <TooltipTrigger asChild>
                  <div
                    id={`shop-skill-slot-${i}`}
                    className="relative overflow-hidden cursor-default"
                    style={{
                      width: slotSize,
                      height: slotSize,
                      borderRadius: 2 * scale,
                      backgroundColor: skill.iconColor,
                      border: `2px solid #22d3ee`,
                    }}
                  >
                    {/* Skill icon image */}
                    {skill.icon && (
                      <img
                        src={skill.icon}
                        alt={skill.name}
                        className="absolute inset-0 w-full h-full object-cover"
                        style={{ borderRadius: 2 * scale }}
                      />
                    )}

                    {/* Hotkey indicator - hide for passive skills */}
                    {!isPassive && (
                      <span
                        className="absolute bottom-0 right-0 text-white font-bold bg-black/50 px-1"
                        style={{ fontSize: 8 * scale, borderRadius: 2 * scale }}
                      >
                        {skill.hotkey}
                      </span>
                    )}

                    {/* Level indicator */}
                    <span
                      className="absolute top-0 left-0 text-amber-400 font-bold bg-black/50 px-1"
                      style={{ fontSize: 8 * scale, borderRadius: 2 * scale }}
                    >
                      {skill.currentLevel}
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top" className="bg-black/95 border border-amber-600/50 shadow-lg shadow-black/50 p-0">
                  <SkillTooltip skill={skill} />
                </TooltipContent>
              </Tooltip>
            )
          }

          // Game variant is interactive with cooldowns and animations
          const cooldownProgress = getSkillCooldownProgress(skill)
          const isOnCooldown = cooldownProgress < 1
          const cooldownSeconds = skill.currentCooldown > 0
            ? Math.ceil(skill.currentCooldown / 60)
            : 0

          const { canUse } = hero ? canUseSkill(hero, skill.id) : { canUse: false }
          const isPending = pendingSkillId === skill.id
          const isJustUpgraded = lastUpgradedSkillId === skill.id
          const isCooldownComplete = cooldownCompleteSkills.has(skill.id)

          return (
            <Tooltip key={i}>
              <TooltipTrigger asChild>
                <button
                  id={`skill-slot-${i}`}
                  className={`relative overflow-hidden transition-all ${
                    isPending
                      ? "ring-2 ring-cyan-400 ring-offset-1 ring-offset-black animate-pulse cursor-crosshair"
                      : canUse
                        ? "hover:brightness-125 cursor-pointer"
                        : "opacity-60 cursor-not-allowed"
                  }`}
                  style={{
                    width: slotSize,
                    height: slotSize,
                    borderRadius: 2 * scale,
                    backgroundColor: skill.iconColor,
                    border: `2px solid ${isPending ? '#22d3ee' : canUse ? '#22d3ee' : '#6b7280'}`,
                  }}
                  onClick={disableClicks ? undefined : () => handleSkillClick(skill)}
                >
                  {/* Skill icon image */}
                  {skill.icon && (
                    <img
                      src={skill.icon}
                      alt={skill.name}
                      className="absolute inset-0 w-full h-full object-cover"
                      style={{ borderRadius: 2 * scale }}
                    />
                  )}
                  {/* Cooldown overlay */}
                  {isOnCooldown && (
                    <div
                      className="absolute inset-0 bg-black/70"
                      style={{
                        clipPath: `polygon(0 ${(1 - cooldownProgress) * 100}%, 100% ${(1 - cooldownProgress) * 100}%, 100% 100%, 0 100%)`,
                      }}
                    />
                  )}

                  {/* Cooldown text */}
                  {isOnCooldown && (
                    <span
                      className="absolute inset-0 flex items-center justify-center text-white font-bold"
                      style={{ fontSize: 12 * scale, textShadow: "0 0 3px black" }}
                    >
                      {cooldownSeconds}
                    </span>
                  )}

                  {/* Hotkey indicator - hide for passive skills and during cooldown */}
                  {!isPassive && !isOnCooldown && (
                    <span
                      className="absolute bottom-0 right-0 text-white font-bold bg-black/50 px-1"
                      style={{ fontSize: 8 * scale, borderRadius: 2 * scale }}
                    >
                      {skill.hotkey}
                    </span>
                  )}

                  {/* Level indicator - hide during cooldown */}
                  {!isOnCooldown && (
                    <span
                      className="absolute top-0 left-0 text-amber-400 font-bold bg-black/50 px-1"
                      style={{ fontSize: 8 * scale, borderRadius: 2 * scale }}
                    >
                      {skill.currentLevel}
                    </span>
                  )}

                  {/* Upgrade particle burst effect */}
                  {isJustUpgraded && (
                    <>
                      {/* Expanding ring */}
                      <div
                        className="absolute inset-0 rounded pointer-events-none animate-[skill-upgrade-ring_0.6s_ease-out_forwards]"
                        style={{
                          border: "3px solid #fbbf24",
                          borderRadius: 2 * scale,
                        }}
                      />
                      {/* Flash overlay */}
                      <div
                        className="absolute inset-0 bg-amber-400/60 pointer-events-none animate-[skill-upgrade-flash_0.3s_ease-out_forwards]"
                        style={{ borderRadius: 2 * scale }}
                      />
                      {/* Particle sparkles */}
                      {[...Array(8)].map((_, pi) => (
                        <div
                          key={pi}
                          className="absolute w-1.5 h-1.5 bg-amber-300 rounded-full pointer-events-none animate-[skill-upgrade-particle_0.6s_ease-out_forwards]"
                          style={{
                            left: "50%",
                            top: "50%",
                            transform: `rotate(${pi * 45}deg) translateY(-${slotSize * 0.8}px)`,
                            animationDelay: `${pi * 30}ms`,
                          }}
                        />
                      ))}
                    </>
                  )}

                  {/* Cooldown complete particle effect - horizontal line sweeping up */}
                  {isCooldownComplete && (
                    <>
                      {/* Sweeping horizontal line */}
                      <div
                        className="absolute left-0 right-0 h-1 pointer-events-none animate-[cooldown-sweep_0.5s_ease-out_forwards]"
                        style={{
                          background: "linear-gradient(90deg, transparent 0%, #22d3ee 20%, #67e8f9 50%, #22d3ee 80%, transparent 100%)",
                          boxShadow: "0 0 8px #22d3ee, 0 0 16px #22d3ee",
                          bottom: 0,
                        }}
                      />
                      {/* Flash overlay */}
                      <div
                        className="absolute inset-0 bg-cyan-400/40 pointer-events-none animate-[cooldown-flash_0.3s_ease-out_forwards]"
                        style={{ borderRadius: 2 * scale }}
                      />
                    </>
                  )}
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="bg-black/95 border border-amber-600/50 shadow-lg shadow-black/50 p-0">
                {hero && <SkillTooltip skill={skill} hero={hero} />}
              </TooltipContent>
            </Tooltip>
          )
        })}
      </div>
    </TooltipProvider>
  )
}
